import { styled } from "goober";
import { nanoid } from "nanoid";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMouseDownOutside } from "../hooks/useMouseDownOutside";
import { playClick } from "../lib/sounds";
import { zIndexes } from "../lib/zIndexes";
import { AnchoredPopover } from "./AnchoredPopover";
import { Box } from "./Box";
import { Icon } from "./Icon";
import { Link } from "./Link";
import { Text } from "./Text";
import { ToggleVisual } from "./Toggle";

type ItemBase = {
  key?: string;
  left: React.ReactNode;
  right?: React.ReactNode;
  label: React.ReactNode;
  searchText?: string;
  subtitle?: React.ReactNode;
  disabled?: boolean;
  muted?: boolean;
  title?: string;
  selected?: boolean;
};

type ButtonItem = {
  type: "button";
  onClick: () => void;
  keepOpen?: boolean;
} & ({ toggled: boolean; right?: never } | { toggled?: undefined });

type LinkItem = {
  type: "link";
  to: string;
  onClick?: () => void;
};

type SubmenuSection = {
  label?: string;
  items: (ItemBase & (ButtonItem | LinkItem))[];
};

type SubmenuItemType = {
  type: "submenu";
  submenu: SubmenuSection[];
  searchable?: boolean;
  searchEmptyLabel?: string;
};

type Item = ItemBase & (ButtonItem | LinkItem | SubmenuItemType);

type Section = {
  label?: string;
  items: Item[];
  sticky?: boolean;
};

const Container = styled("div")`
  position: relative;
`;

const Opener = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
`;

const Popover = styled(AnchoredPopover)`
  background: var(--surface-primary);
  box-shadow: var(--elevation-high);
  outline: 0.5px solid var(--border-alpha);
  z-index: ${zIndexes.dropdown};
  min-width: 208px;
  max-width: 320px;
  max-height: min(300px, calc(100dvh / var(--ui-zoom, 1) - 16px));
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const StickyArea = styled("div")`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px 4px 0;
`;

const ScrollArea = styled("div")`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: none;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px;
`;

function scrollWithin(container: HTMLElement, target: HTMLElement) {
  const c = container.getBoundingClientRect();
  const t = target.getBoundingClientRect();
  const targetCenter = t.top + t.height / 2;
  const containerCenter = c.top + c.height / 2;
  container.scrollTop += targetCenter - containerCenter;
}

const LeftIconContainer = styled("div")`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RightContainer = styled("div")`
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

function rightSlot(item: Item) {
  if (item.type === "button" && item.toggled !== undefined)
    return <ToggleVisual isOn={item.toggled} />;
  return item.right;
}

function switchProps(item: Item) {
  if (item.type !== "button" || item.toggled === undefined) return undefined;
  return {
    role: "switch",
    "aria-checked": item.toggled,
    "aria-disabled": item.disabled,
  } as const;
}

const alignStyles = {
  bottom: {
    top: "anchor(bottom)",
    right: "anchor(right)",
    positionTryFallbacks:
      "flip-block, flip-inline, flip-block flip-inline, --dropdown-fit-viewport, --dropdown-fit-viewport flip-block",
  },
  "bottom-left": {
    top: "anchor(bottom)",
    left: "anchor(left)",
    positionTryFallbacks: "flip-block",
  },
  "bottom-center": {
    top: "anchor(bottom)",
    left: "anchor(center)",
    translate: "-50% 0",
    positionTryFallbacks: "flip-block",
  },
  top: {
    bottom: "anchor(top)",
    right: "anchor(right)",
    positionTryFallbacks: "flip-block",
  },
  "top-left": {
    bottom: "anchor(top)",
    left: "anchor(left)",
    positionTryFallbacks: "flip-block",
  },
  right: {
    top: "anchor(top)",
    left: "anchor(right)",
    positionTryFallbacks: "flip-inline, flip-block, flip-inline flip-block",
  },
  left: {
    top: "anchor(top)",
    right: "anchor(left)",
    positionTryFallbacks: "flip-inline, flip-block, flip-inline flip-block",
  },
  "left-center": {
    top: "anchor(center)",
    right: "anchor(left)",
    translate: "0 -50%",
    positionTryFallbacks: "flip-inline",
  },
} as const;

const alignToDirection = {
  bottom: "down",
  "bottom-left": "down",
  "bottom-center": "down",
  top: "up",
  "top-left": "up",
  right: "right",
  left: "left",
  "left-center": "left",
} as const;

const Divider = styled("div")`
  height: 0.5px;
  margin: 4px 8px;
  background: var(--border-alpha);
`;

const FullWidthDivider = styled("div")`
  height: 0.5px;
  margin: 2px -4px 0;
  background: var(--border-alpha);
  flex-shrink: 0;
`;

const displayedItemStyle = `
  flex: 1;
  width: 100%;
  align-items: center;
  display: flex;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  border: 0;
  outline: 0;
  text-decoration: none;
  background: transparent;
  color: var(--text-primary);
  &:hover {
    background: var(--alpha4);
  }
  [data-kb-nav] &:hover:not([data-highlighted]) {
    background: transparent;
  }
  &[data-highlighted] {
    background: var(--alpha4);
  }
`;

const DisplayedItemButton = styled("button")`
  ${displayedItemStyle}
`;
const DisplayedItemLink = styled(Link)`
  ${displayedItemStyle}
`;

const SUBMENU_MAX_WIDTH = 280;

const SubmenuPopoverContainer = styled("div")`
  position: fixed;
  border-radius: 12px;
  background: var(--surface-primary);
  box-shadow: var(--elevation-high);
  outline: 0.5px solid var(--border-alpha);
  z-index: ${zIndexes.dropdown};
  min-width: 208px;
  max-width: ${SUBMENU_MAX_WIDTH}px;
  max-height: 300px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const SubmenuEmptyState = styled("div")`
  flex: 1;
  min-height: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
  padding: 16px;
`;

const SubmenuScrollArea = styled("div")`
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: none;
  padding: 4px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SubmenuSearchRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 12px;
  border-bottom: 0.5px solid var(--border-alpha);
  flex-shrink: 0;
`;

const SubmenuSearchInput = styled("input")`
  all: unset;
  flex: 1;
  min-width: 0;
  font-weight: 350;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: -0.01em;
  color: var(--text-primary);
  &::placeholder {
    color: var(--text-primary);
    opacity: 0.3;
  }
`;

type Point = { x: number; y: number };

function pointInTriangle(p: Point, a: Point, b: Point, c: Point) {
  const sign = (p1: Point, p2: Point, p3: Point) =>
    (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
  const d1 = sign(p, a, b);
  const d2 = sign(p, b, c);
  const d3 = sign(p, c, a);
  const hasNeg = d1 < 0 || d2 < 0 || d3 < 0;
  const hasPos = d1 > 0 || d2 > 0 || d3 > 0;
  return !(hasNeg && hasPos);
}

function SubmenuRow({
  item,
  itemKey,
  rowAnchorName,
  style,
  simulateMouseDownOutside,
  minWidth,
  isOpen,
  onRowEnter,
  onRowLeave,
  onPopoverEnter,
  onPopoverLeave,
  onClose,
  popoverRefCb,
}: {
  item: ItemBase & SubmenuItemType;
  itemKey: string;
  rowAnchorName: string;
  style: React.CSSProperties;
  simulateMouseDownOutside: () => void;
  minWidth?: number;
  isOpen: boolean;
  onRowEnter: (e: React.MouseEvent) => void;
  onRowLeave: () => void;
  onPopoverEnter: () => void;
  onPopoverLeave: () => void;
  onClose: () => void;
  popoverRefCb: (el: HTMLDivElement | null) => void;
}) {
  const parentButtonRef = useRef<HTMLButtonElement>(null);
  const popoverElRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");

  if (!isOpen && query !== "") setQuery("");

  useEffect(() => {
    if (!isOpen) return;
    const popover = popoverElRef.current;
    if (!popover) return;

    popover.tabIndex = -1;

    if (item.searchable) {
      searchInputRef.current?.focus({ preventScroll: true });
    } else {
      requestAnimationFrame(() => popover.focus({ preventScroll: true }));
    }

    const getItems = () =>
      Array.from(
        popover.querySelectorAll<HTMLElement>(
          "button:not([disabled]), a:not([disabled])"
        )
      );
    const clearHighlight = () => {
      popover.querySelectorAll("[data-highlighted]").forEach((el) => {
        el.removeAttribute("data-highlighted");
      });
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "ArrowLeft") {
        e.stopPropagation();
        e.preventDefault();
        onClose();
        parentButtonRef.current?.focus({ preventScroll: true });
        return;
      }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      e.stopPropagation();
      e.preventDefault();
      popover.setAttribute("data-kb-nav", "");
      const items = getItems();
      if (items.length === 0) return;
      const active = document.activeElement as HTMLElement | null;
      const onInput = item.searchable && active === searchInputRef.current;
      const current = onInput ? -1 : items.indexOf(active as HTMLElement);
      let next: number;
      if (current === -1) {
        next = e.key === "ArrowDown" ? 0 : items.length - 1;
      } else if (e.key === "ArrowDown") {
        next = current < items.length - 1 ? current + 1 : 0;
      } else {
        if (current === 0 && item.searchable && searchInputRef.current) {
          clearHighlight();
          searchInputRef.current.focus();
          return;
        }
        next = current > 0 ? current - 1 : items.length - 1;
      }
      clearHighlight();
      items[next]?.setAttribute("data-highlighted", "");
      items[next]?.focus({ preventScroll: true });
    };

    popover.addEventListener("keydown", onKeyDown);
    return () => popover.removeEventListener("keydown", onKeyDown);
  }, [isOpen, item.searchable, onClose]);

  const [container, setContainer] = useState<Element | null>(null);
  const resolveWrapper = (el: HTMLDivElement | null) => {
    if (el)
      setContainer(el.closest("[data-popover-host], dialog") ?? document.body);
  };

  const lowerQuery = query.trim().toLowerCase();
  const filteredSubmenu = lowerQuery
    ? item.submenu.map((section) => ({
        ...section,
        items: section.items.filter((sub) => {
          const haystack =
            sub.searchText ?? (typeof sub.label === "string" ? sub.label : "");
          return haystack.toLowerCase().includes(lowerQuery);
        }),
      }))
    : item.submenu;

  const hasResults = filteredSubmenu.some(
    (section) => section.items.length > 0
  );

  return (
    <div ref={resolveWrapper} style={{ display: "contents" }}>
      <DisplayedItemButton
        ref={parentButtonRef}
        data-submenu-key={item.disabled ? undefined : itemKey}
        style={isOpen ? { ...style, background: "var(--alpha4)" } : style}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseEnter={item.disabled ? undefined : onRowEnter}
        onMouseLeave={item.disabled ? undefined : onRowLeave}
      >
        {item.left != null && (
          <LeftIconContainer>{item.left}</LeftIconContainer>
        )}
        <Text variant="text-regular" color="var(--text-primary)" ellipsis>
          {item.label}
        </Text>
        {!item.disabled && (
          <RightContainer>
            <Icon name="chevron-right" size={12} />
          </RightContainer>
        )}
      </DisplayedItemButton>
      {!item.disabled &&
        isOpen &&
        container &&
        createPortal(
          <SubmenuPopoverContainer
            ref={(el) => {
              popoverElRef.current = el;
              popoverRefCb(el);
            }}
            data-popover
            style={
              {
                positionAnchor: rowAnchorName,
                top: "anchor(top)",
                left: "anchor(right)",
                positionTryFallbacks:
                  "flip-inline, flip-block, flip-inline flip-block",
                margin: "-4px 6px",
                ...(minWidth !== undefined ? { minWidth } : {}),
                ...(item.searchable ? { width: SUBMENU_MAX_WIDTH } : {}),
              } as React.CSSProperties
            }
            onMouseEnter={() => {
              onPopoverEnter();
              if (item.searchable) {
                searchInputRef.current?.focus({ preventScroll: true });
              }
            }}
            onMouseLeave={onPopoverLeave}
          >
            {item.searchable && (
              <SubmenuSearchRow>
                <Icon name="search" size={12} color="var(--text-primary)" />
                <SubmenuSearchInput
                  ref={searchInputRef}
                  autoFocus
                  placeholder="Search…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onMouseDown={(e) => e.stopPropagation()}
                />
              </SubmenuSearchRow>
            )}
            <SubmenuScrollArea>
              {filteredSubmenu.flatMap((section, si) => {
                const elements: React.ReactNode[] = [];
                const sectionKey =
                  section.label ?? section.items[0]?.label ?? `s${si}`;
                const prevSection = filteredSubmenu[si - 1];
                if (prevSection && prevSection.items.length > 0) {
                  elements.push(<Divider key={`divider-${sectionKey}`} />);
                }
                if (section.label) {
                  elements.push(
                    <Box
                      key={`label-${sectionKey}`}
                      horizontal={8}
                      vertical={6}
                    >
                      <Text variant="text-small" color="var(--text-secondary)">
                        {section.label}
                      </Text>
                    </Box>
                  );
                }
                for (const [idx, subItem] of section.items.entries()) {
                  const key =
                    subItem.key ??
                    (typeof subItem.label === "string"
                      ? subItem.label
                      : `s${idx}`);
                  const subRight = rightSlot(subItem);
                  const subChildren = (
                    <>
                      {subItem.left != null && (
                        <LeftIconContainer>{subItem.left}</LeftIconContainer>
                      )}
                      <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                        <Text
                          variant="text-regular"
                          color="var(--text-primary)"
                          ellipsis
                        >
                          {subItem.label}
                        </Text>
                        {subItem.subtitle != null && subItem.subtitle}
                      </div>
                      {subRight != null && (
                        <RightContainer>{subRight}</RightContainer>
                      )}
                    </>
                  );
                  if (subItem.type === "button") {
                    const handle = () => {
                      if (subItem.disabled) return;
                      playClick();
                      subItem.onClick();
                      simulateMouseDownOutside();
                    };
                    elements.push(
                      <DisplayedItemButton
                        key={key}
                        {...switchProps(subItem)}
                        style={subItem.disabled ? { opacity: 0.5 } : undefined}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          handle();
                        }}
                        onClick={(e) => {
                          if (e.detail === 0) handle();
                        }}
                      >
                        {subChildren}
                      </DisplayedItemButton>
                    );
                  } else if (subItem.type === "link") {
                    elements.push(
                      <DisplayedItemLink
                        key={key}
                        style={subItem.disabled ? { opacity: 0.5 } : undefined}
                        to={subItem.to}
                        onClick={() => {
                          subItem.onClick?.();
                          simulateMouseDownOutside();
                        }}
                      >
                        {subChildren}
                      </DisplayedItemLink>
                    );
                  }
                }
                return elements;
              })}
              {lowerQuery && !hasResults && (
                <SubmenuEmptyState>
                  <Icon
                    name="text-search"
                    size={24}
                    color="var(--text-secondary)"
                  />
                  <Text variant="text-small" color="var(--text-secondary)">
                    {item.searchEmptyLabel ?? "No results"}
                  </Text>
                </SubmenuEmptyState>
              )}
            </SubmenuScrollArea>
          </SubmenuPopoverContainer>,
          container
        )}
    </div>
  );
}

type Props = {
  opener: React.ReactNode;
  sections: Section[];
  margin?: number | string;
  align?:
    | "bottom"
    | "bottom-center"
    | "bottom-left"
    | "top"
    | "top-left"
    | "left"
    | "left-center"
    | "right";
  target?: string;
  minWidth?: number;
  maxWidth?: number;
  maxHeight?: number | string;
  onOpenChange?: (open: boolean) => void;
};

export function Dropdown({
  opener,
  sections,
  margin = 4,
  align = "bottom",
  target,
  minWidth,
  maxWidth,
  maxHeight,
  onOpenChange,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [containerRef, portalRef] = useMouseDownOutside(() => setIsOpen(false));
  const [openSubmenuKey, setOpenSubmenuKey] = useState<string | null>(null);
  const submenuPopoverRefs = useRef(new Map<string, HTMLDivElement>());
  const rowCloseTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const switchSafetyTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const triangleListener = useRef<((e: MouseEvent) => void) | null>(null);
  const [popoverAnchorName] = useState(() => `--dropdown-popover-${nanoid()}`);

  const clearTriangleTracking = useCallback(() => {
    if (triangleListener.current) {
      document.removeEventListener("mousemove", triangleListener.current);
      triangleListener.current = null;
    }
    clearTimeout(switchSafetyTimer.current);
  }, []);

  if (!isOpen && openSubmenuKey !== null) setOpenSubmenuKey(null);

  useEffect(() => {
    if (isOpen) return;
    clearTriangleTracking();
    clearTimeout(rowCloseTimer.current);
  }, [isOpen, clearTriangleTracking]);

  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    return () => {
      clearTriangleTracking();
      clearTimeout(rowCloseTimer.current);
    };
  }, [clearTriangleTracking]);

  const handleRowEnter = (key: string, e: React.MouseEvent) => {
    clearTimeout(rowCloseTimer.current);

    if (openSubmenuKey === key) {
      clearTriangleTracking();
      return;
    }

    if (openSubmenuKey === null) {
      clearTriangleTracking();
      setOpenSubmenuKey(key);
      return;
    }

    const popover = submenuPopoverRefs.current.get(openSubmenuKey);
    if (!popover) {
      clearTriangleTracking();
      setOpenSubmenuKey(key);
      return;
    }

    const rect = popover.getBoundingClientRect();
    const entry = { x: e.clientX, y: e.clientY };
    const opensRight = rect.left >= entry.x;
    const nearX = opensRight ? rect.left : rect.right;
    const maxSpread = 100;
    const topY = Math.max(rect.top, entry.y - maxSpread);
    const botY = Math.min(rect.bottom, entry.y + maxSpread);
    const topCorner = { x: nearX, y: topY };
    const botCorner = { x: nearX, y: botY };

    const commitSwitch = () => {
      clearTriangleTracking();
      setOpenSubmenuKey(key);
    };

    clearTriangleTracking();
    const onMove = (ev: MouseEvent) => {
      const p = { x: ev.clientX, y: ev.clientY };
      if (!pointInTriangle(p, entry, topCorner, botCorner)) {
        commitSwitch();
      }
    };
    triangleListener.current = onMove;
    document.addEventListener("mousemove", onMove);
    switchSafetyTimer.current = setTimeout(commitSwitch, 500);
  };

  const handleRowLeave = () => {
    clearTimeout(rowCloseTimer.current);
    rowCloseTimer.current = setTimeout(() => {
      clearTriangleTracking();
      setOpenSubmenuKey(null);
    }, 150);
  };

  const handlePopoverEnter = () => {
    clearTimeout(rowCloseTimer.current);
    clearTriangleTracking();
  };

  const handlePopoverLeave = () => {
    clearTimeout(rowCloseTimer.current);
    rowCloseTimer.current = setTimeout(() => setOpenSubmenuKey(null), 150);
  };

  const toggleOpen = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsOpen((was) => !was);
  };

  const simulateMouseDownOutside = () => {
    document.body.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
  };

  useEffect(() => {
    if (!isOpen) return;
    const popover = portalRef.current;
    if (!popover) return;
    const container = containerRef.current;

    const getItems = () =>
      Array.from(
        popover.querySelectorAll<HTMLElement>(
          "button:not([disabled]), a:not([disabled])"
        )
      );

    popover.tabIndex = -1;

    requestAnimationFrame(() => {
      popover.focus({ preventScroll: true });
      const selected = popover.querySelector<HTMLElement>("[data-selected]");
      const scrollArea = selected?.closest<HTMLElement>("[data-scroll-area]");
      if (selected && scrollArea) scrollWithin(scrollArea, selected);
    });

    const clearHighlight = () => {
      popover.querySelectorAll("[data-highlighted]").forEach((el) => {
        el.removeAttribute("data-highlighted");
      });
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        e.preventDefault();
        setIsOpen(false);
        return;
      }
      if (e.key === "ArrowRight" || e.key === "Enter") {
        const active = document.activeElement as HTMLElement | null;
        const submenuKey = active?.getAttribute("data-submenu-key");
        if (submenuKey) {
          e.stopPropagation();
          e.preventDefault();
          setOpenSubmenuKey(submenuKey);
          return;
        }
        if (e.key === "ArrowRight") return;
      }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      e.stopPropagation();
      e.preventDefault();
      popover.setAttribute("data-kb-nav", "");
      const items = getItems();
      if (items.length === 0) return;
      const current = items.indexOf(document.activeElement as HTMLElement);
      let next: number;
      if (current === -1) {
        // Switching from mouse to keyboard: highlight the hovered item
        const hovered = items.findIndex((item) => item.matches(":hover"));
        next = hovered !== -1 ? hovered : 0;
      } else if (e.key === "ArrowDown") {
        next = current < items.length - 1 ? current + 1 : 0;
      } else {
        next = current > 0 ? current - 1 : items.length - 1;
      }
      clearHighlight();
      items[next]?.setAttribute("data-highlighted", "");
      items[next]?.focus({ preventScroll: true });
    };

    const onMouseMove = () => {
      popover.removeAttribute("data-kb-nav");
      clearHighlight();
      popover.focus({ preventScroll: true });
    };

    popover.addEventListener("keydown", onKeyDown);
    popover.addEventListener("mousemove", onMouseMove);
    return () => {
      popover.removeEventListener("keydown", onKeyDown);
      popover.removeEventListener("mousemove", onMouseMove);
      // Closing unmounts the focused popover, dropping focus to <body> and
      // breaking tab flow; hand focus back to the opener unless it already
      // moved somewhere deliberate (e.g. a mousedown on another field).
      if (document.activeElement === document.body) {
        container
          ?.querySelector<HTMLElement>("button, a, [tabindex]")
          ?.focus({ preventScroll: true });
      }
    };
  }, [isOpen, portalRef, containerRef]);

  return (
    <Container ref={containerRef}>
      <Popover
        style={
          {
            margin,
            ...alignStyles[align],
            ...(minWidth !== undefined ? { minWidth } : {}),
            ...(maxWidth !== undefined ? { maxWidth } : {}),
            ...(maxHeight !== undefined ? { maxHeight } : {}),
          } as React.CSSProperties
        }
        direction={alignToDirection[align]}
        isOpen={isOpen}
        popoverRef={portalRef}
        target={target}
        popoverAnchorName={popoverAnchorName}
        trigger={(props) => (
          <Opener
            onMouseDown={(e) => {
              playClick();
              toggleOpen(e);
            }}
            onClick={(e) => {
              if (e.detail === 0) {
                playClick();
                toggleOpen(e);
              }
            }}
            {...props}
          >
            {opener}
          </Opener>
        )}
      >
        {(() => {
          type RenderedSection = {
            key: string;
            inner: React.ReactNode;
          };
          const sticky: RenderedSection[] = [];
          const scroll: RenderedSection[] = [];
          sections.forEach((section, i) => {
            if (section.items.length === 0) return;
            const firstItemLabel = section.items[0]?.label;
            const sectionKey =
              section.label ??
              (typeof firstItemLabel === "string" ? firstItemLabel : `s${i}`);
            const inner = (
              <>
                {section.label && (
                  <Box horizontal={8} vertical={8}>
                    <Text variant="text-small" color="var(--text-secondary)">
                      {section.label}
                    </Text>
                  </Box>
                )}
                {section.items.map((item, itemIndex) => {
                  const key =
                    item.key ??
                    (typeof item.label === "string"
                      ? item.label
                      : `i${i}-${itemIndex}`);
                  const rowAnchorName = `--dropdown-row-${i}-${itemIndex}`;
                  const rightContent = rightSlot(item);
                  const sharedProps = {
                    title: item.title,
                    "data-selected": item.selected ? "" : undefined,
                    ...switchProps(item),
                    style: {
                      ...(item.disabled ? { opacity: 0.5 } : {}),
                      anchorName: rowAnchorName,
                    } as React.CSSProperties,
                  };
                  const children = (
                    <>
                      {item.left != null && (
                        <LeftIconContainer>{item.left}</LeftIconContainer>
                      )}
                      <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                        <Text
                          variant={item.muted ? "text-small" : "text-regular"}
                          color={
                            item.muted
                              ? "var(--text-secondary)"
                              : "var(--text-primary)"
                          }
                          ellipsis
                        >
                          {item.label}
                        </Text>
                        {item.subtitle != null && item.subtitle}
                      </div>
                      <RightContainer>{rightContent}</RightContainer>
                    </>
                  );

                  if (item.type === "submenu") {
                    return (
                      <SubmenuRow
                        key={key}
                        item={item}
                        itemKey={key}
                        rowAnchorName={rowAnchorName}
                        style={sharedProps.style}
                        simulateMouseDownOutside={simulateMouseDownOutside}
                        minWidth={minWidth}
                        isOpen={openSubmenuKey === key}
                        onRowEnter={(e) => handleRowEnter(key, e)}
                        onRowLeave={handleRowLeave}
                        onPopoverEnter={handlePopoverEnter}
                        onPopoverLeave={handlePopoverLeave}
                        onClose={() => setOpenSubmenuKey(null)}
                        popoverRefCb={(el) => {
                          if (el) submenuPopoverRefs.current.set(key, el);
                          else submenuPopoverRefs.current.delete(key);
                        }}
                      />
                    );
                  }
                  if (item.type === "button") {
                    const handle = () => {
                      if (item.disabled) return;
                      playClick();
                      item.onClick();
                      if (!item.keepOpen) simulateMouseDownOutside();
                    };
                    return (
                      <DisplayedItemButton
                        key={key}
                        {...sharedProps}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          handle();
                        }}
                        onClick={(e) => {
                          if (e.detail === 0) handle();
                        }}
                      >
                        {children}
                      </DisplayedItemButton>
                    );
                  }
                  return (
                    <DisplayedItemLink
                      key={key}
                      {...sharedProps}
                      to={item.to}
                      onClick={() => {
                        item.onClick?.();
                        simulateMouseDownOutside();
                      }}
                    >
                      {children}
                    </DisplayedItemLink>
                  );
                })}
              </>
            );
            (section.sticky ? sticky : scroll).push({
              key: sectionKey,
              inner,
            });
          });

          return (
            <>
              {sticky.length > 0 && (
                <StickyArea>
                  {sticky.map((s) => (
                    <Fragment key={s.key}>{s.inner}</Fragment>
                  ))}
                  {scroll.length > 0 && <FullWidthDivider />}
                </StickyArea>
              )}
              <ScrollArea data-scroll-area="">
                {scroll.map((s, idx) => (
                  <Fragment key={s.key}>
                    {idx > 0 && <Divider />}
                    {s.inner}
                  </Fragment>
                ))}
              </ScrollArea>
            </>
          );
        })()}
      </Popover>
    </Container>
  );
}
